/**
 * Derive the **issuing branch code** for display on the Card / Chequebook
 * secure-address journeys (CCF-77, issues #188 / #189 / #154).
 *
 * Authoritative ruling (@dylan219, 2026-07-01): "If the issuing branch code is
 * available, just display it to the agent — it is the first 6 digits of their
 * account number after the PTSB prefix. Use this SAME method everywhere we have
 * a check for Y/N on the secure address." This is the single derivation used by
 * every card postal journey (Card Maintenance secure-address panel) and the
 * Chequebook journey.
 *
 * The "PTSB prefix" is the institution identifier that can lead an account /
 * IBAN identifier (e.g. the `IE##IPBS` IBAN prefix, or a literal `PTSB`); we
 * strip it, then take the first 6 digits of the remaining account number. The
 * mock's bare 8-digit account numbers (`90210011`) carry no prefix, so we simply
 * take their leading 6 digits (`902100`).
 *
 * This is a **client-side derivation from the account number the screen already
 * holds** — it is deliberately NOT a gateway branch-lookup call (#154 removed
 * the eager fetch + "Could not load the issuing branch" error). It is also
 * DISPLAY-ONLY: it never overwrites the postal address (#189 — the postal
 * address is left untouched when the secure flag is N).
 *
 * Returns `undefined` when fewer than 6 digits are available (so the caller
 * omits the field rather than showing a misleading partial).
 */
export function deriveIssuingBranchCode(
  accountNumber: string | undefined | null,
): string | undefined {
  if (!accountNumber) return undefined;
  // Strip a leading PTSB institution prefix: either a literal `PTSB` or an
  // IBAN-style `IE##IPBS` / `IE##XXXX` prefix, then keep only the digits.
  const withoutIban = accountNumber
    .trim()
    .replace(/^IE\d{2}[A-Z]{4}/i, '')
    .replace(/^PTSB/i, '');
  const digits = withoutIban.replace(/\D/g, '');
  if (digits.length < 6) return undefined;
  return digits.slice(0, 6);
}
